<template>
    <div
        v-on-clickaway="handleClickAway"
        :class="dropdownClass"
        class="Dropdown"
    >
        <a
            id="btn"
            v-tooltip="tooltip"
            href="#"
            data-cy="dropdownBtn"
            class="Btn"
            @click.prevent="handleClickBtn"
        >
            <slot>
                {{ text }}
            </slot>
            <WIcon
                v-if="icon"
                :color="iconColor"
                :icon="icon"
                :size="iconSize"
            />
        </a>

        <Transition
            name="fade"
            :duration="duration"
        >
            <div :style="coordinatesStyles">
                <WCard
                    v-if="openedInternal"
                    class="Menu"
                    :style="{ maxHeight: maxHeight ? `${maxHeight}px` : 'initial' }"
                >
                    <a
                        v-for="option in options"
                        :id="option.key"
                        :key="option.key"
                        :data-cy="option.key"
                        href="#"
                        class="Link Option"
                        :class="option.style"
                        @click.prevent="handleClickOption(option)"
                    >
                        <div class="flex row-left-center">
                            <WIcon
                                v-if="option.icon"
                                :icon="option.icon"
                                :size="18"
                            />
                            <WText
                                tag="span"
                                :size="14"
                                class="mb-0"
                            >{{ option.label }}</WText>
                            <WIcon
                                v-if="option.iconRight"
                                :icon="option.iconRight"
                                :size="14"
                            />
                        </div>
                    </a>
                </WCard>
            </div>
        </Transition>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { mixin as clickawayMixin } from 'vue-clickaway'

export default {
    name: 'Dropdown',
    mixins: [clickawayMixin],
    props: {
        opened: VueTypes.bool.def(false),
        text: VueTypes.string,
        options: VueTypes.arrayOf(VueTypes.shape({
            key: VueTypes.oneOfType([Number, String]).isRequired,
            label: VueTypes.oneOfType([Number, String]).isRequired,
        }).loose).isRequired,
        icon: VueTypes.string.def(''),
        iconSize: VueTypes.number.def(14),
        iconColor: VueTypes.oneOf(['primary', 'secondary', 'tertiary', 'success', 'warning', 'info', 'error', 'none', 'link', 'accent-blue']).def('primary'),
        direction: VueTypes.oneOf(['topLeft', 'topRight', 'bottomLeft', 'bottomRight']).def('bottomLeft'),
        withCoordinates: VueTypes.bool.def(false),
        duration: VueTypes.number.def(300),
        maxHeight: VueTypes.number,
        tooltip: VueTypes.any,
    },
    data () {
        return {
            openedInternal: false,
            coordinates: undefined,
        }
    },
    computed: {
        dropdownClass () {
            return [
                this.icon,
                this.direction,
            ]
        },
        coordinatesStyles () {
            return this.coordinates ? {
                position: 'fixed',
                zIndex: '10000',
                left: `${this.coordinates.x}px`,
                top: `${this.coordinates.y}px`,
            } : ''
        },
    },
    watch: {
        opened: {
            handler (value) {
                this.openedInternal = value
            },
            immediate: true,
        },
    },
    methods: {
        handleClickBtn ({ clientX: x, clientY: y }) {
            if (this.withCoordinates) {
                this.coordinates = { x, y }
            }
            this.openedInternal = !this.openedInternal
        },
        handleClickOption (option) {
            this.$emit('onClick', option)
            this.openedInternal = false
        },
        handleClickAway () {
            if (this.openedInternal) {
                this.openedInternal = false
                this.$emit('onClose')
            }
        },
    },
}
</script>

<style scoped lang="scss">
.Dropdown {
    position: relative;
}

.Menu {
    position: absolute;
    display: block;
    padding: 4px 0;
    right: 0;
    min-width: 180px;
    margin: 8px 0;
    overflow-y: auto;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.2);
    z-index: 100000;

    &::v-deep .Body {
        padding: 0;
    }

    // Positions
    .topLeft &,
    .topRight & {
        bottom: 100%;
        top: auto;
    }
    .bottomLeft &,
    .bottomRight & {
        top: 100%;
        bottom: auto;
    }
    .topRight &,
    .bottomRight & {
        left: 0;
        right: auto;
    }
    .topLeft &,
    .bottomLeft & {
        right: 0;
        left: auto;
    }
}

.Link {
    display: block;
    color: $link-color;
    padding: 6px 8px;

    &.Option {
        &:hover {
            background: $gray-06;
        }
    }

    .WIcon {
        margin-right: 10px;
    }

    .WText {
        line-height: 1;

        & + .WIcon {
            margin-left: 10px;
        }
    }

    &.separator, &.separator-bottom {
        border-bottom: 1px solid $gray-05;
    }

    &.separator-top {
        border-top: 1px solid $gray-05;
    }
}
</style>
